import { lastFindableLog } from "./flightlog";
import { GROUND_MOVING_KT, hydrateFlightPhaseId, phaseIsAirborne, type FlightPhaseId } from "./flightPhase";
import { formatFuelFigure, fuelUsedToUsGal, prefillFuelUsedFromSimQty, resolveAircraftFuelUnit, type FuelUnit } from "./fuelUnits";
import type { SimFuelActual } from "./ofpTicketFuel";
import type { PositionSource } from "./position";
import { liveSessionAsDraft, type EngineTimeSource, type TailTelemetrySession, type TailTripTelemetry } from "./simTelemetry";
import type { AircraftSpec, FlightLogEntry, Mission } from "./types";

/**
 * Compact SimConnect phase / speed sample for post-flight drafts.
 * Desk records these from live SimConnect only — never SI or last-known.
 */
export type SimPhaseSample = {
  atUtc: string;
  phase: FlightPhaseId;
  onGround: boolean | null;
  groundSpeed: number | null;
  airspeed: number | null;
  source: PositionSource;
  fuelTotalGal?: number | null;
  fuelUsedGal?: number | null;
};

export type SimTimeDraft = {
  source: "simconnect";
  airTimeHours: number | null;
  engineHours: number | null;
  /** Exact per-engine run, m:ss. The prefilled engine number stays in tenths. */
  engineDetail?: string | null;
  groundMovementHours: number | null;
  blockHours: number | null;
  apuHours?: number | null;
  fuelUsedAmount?: number | null;
  fuelUsedUnit?: FuelUnit;
  engineSource?: EngineTimeSource;
  offBlocksUtc?: string;
  airborneUtc?: string;
  landedUtc?: string;
  onBlocksUtc?: string;
  note: string;
  sampleCount: number;
  fuelUpGal?: number | null;
  fuelDownGal?: number | null;
  fuelUsedGal?: number | null;
};

export type SimLogDraft = SimTimeDraft & {
  originIcao?: string;
  destIcao?: string;
  registration?: string;
  callsign?: string;
  flightLogId?: string;
  aircraftId?: string;
};

/** Keep ~2 h at 30 s, or more when phase changes. */
export const SIM_HISTORY_MAX = 240;
export const SIM_HISTORY_MIN_MS = 5_000;
/** Groundspeed that counts as taxi / block movement. */
export const SIM_TAXI_KT = GROUND_MOVING_KT;
/** Do not invent a draft from a couple of ticks. */
export const SIM_DRAFT_MIN_SPAN_MS = 30_000;
/** Drop parked / mixed intervals across a pause (sim pause / reconnect). */
export const SIM_DRAFT_GAP_MS = 15 * 60_000;
/** Sparse cruise still counts — polls are not once a minute. */
export const SIM_DRAFT_AIR_GAP_MS = 8 * 3600_000;
export const SIM_DRAFT_TAXI_GAP_MS = 45 * 60_000;

function asNum(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return null;
}

function sampleTime(atUtc: string): number {
  const t = new Date(atUtc).getTime();
  return Number.isFinite(t) ? t : Number.NaN;
}

function hoursFromMs(ms: number): number {
  return Math.round((ms / 3_600_000) * 100) / 100;
}

export function formatDraftHours(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return "";
  const rounded = Math.round(hours * 100) / 100;
  return String(rounded);
}

export function hydrateSimPhaseSample(raw?: unknown): SimPhaseSample | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const atUtc = typeof row.atUtc === "string" ? row.atUtc : "";
  if (!atUtc || !Number.isFinite(sampleTime(atUtc))) return null;
  const phase = hydrateFlightPhaseId(row.phase);
  if (!phase) return null;
  const source =
    row.source === "simconnect" || row.source === "sayintentions" || row.source === "last_known"
      ? row.source
      : null;
  if (source !== "simconnect") return null;
  const onGround =
    row.onGround === true ? true : row.onGround === false ? false : row.onGround === null ? null : null;
  return {
    atUtc,
    phase,
    onGround,
    groundSpeed: asNum(row.groundSpeed),
    airspeed: asNum(row.airspeed),
    source,
    fuelTotalGal: asNum(row.fuelTotalGal),
    fuelUsedGal: asNum(row.fuelUsedGal),
  };
}

export function hydrateSimPhaseHistory(raw?: unknown): SimPhaseSample[] {
  if (!Array.isArray(raw)) return [];
  const out: SimPhaseSample[] = [];
  for (const row of raw) {
    const sample = hydrateSimPhaseSample(row);
    if (sample) out.push(sample);
  }
  return out.length > SIM_HISTORY_MAX ? out.slice(out.length - SIM_HISTORY_MAX) : out;
}

function speedKt(sample: Pick<SimPhaseSample, "groundSpeed" | "airspeed">): number {
  const gs = asNum(sample.groundSpeed);
  if (gs != null) return Math.abs(gs);
  const ias = asNum(sample.airspeed);
  return ias != null ? Math.abs(ias) : 0;
}

export function sampleIsAirborne(sample: SimPhaseSample): boolean {
  if (sample.onGround === false) return true;
  if (sample.onGround === true) return false;
  return phaseIsAirborne(sample.phase);
}

export function sampleIsMoving(sample: SimPhaseSample): boolean {
  if (sampleIsAirborne(sample)) return true;
  if (sample.phase === "taxi" || sample.phase === "takeoff") return true;
  return speedKt(sample) >= SIM_TAXI_KT;
}

export function appendSimPhaseSample(history: SimPhaseSample[], sample: SimPhaseSample): SimPhaseSample[] {
  const row = hydrateSimPhaseSample(sample);
  if (!row) return history;
  const last = history[history.length - 1];
  if (last) {
    const dt = sampleTime(row.atUtc) - sampleTime(last.atUtc);
    if (!Number.isFinite(dt) || dt < 0) return history;
    const samePhase = last.phase === row.phase && last.onGround === row.onGround;
    const sameSpeed = Math.round(speedKt(last)) === Math.round(speedKt(row));
    const sameFuel = last.fuelTotalGal === row.fuelTotalGal && last.fuelUsedGal === row.fuelUsedGal;
    if (samePhase && sameSpeed && sameFuel && dt < SIM_HISTORY_MIN_MS) return history;
  }
  const next = history.length >= SIM_HISTORY_MAX ? [...history.slice(history.length - SIM_HISTORY_MAX + 1), row] : [...history, row];
  return next;
}

function classify(sample: SimPhaseSample): "air" | "taxi" | "parked" {
  if (sampleIsAirborne(sample)) return "air";
  if (sampleIsMoving(sample)) return "taxi";
  return "parked";
}

/**
 * Engine ≈ taxi + air (first movement → last stop). Air is wheels-off.
 * APU is not estimated — SimConnect fields here have no APU running flag.
 */
export function estimateSimTimes(history: SimPhaseSample[], now = new Date()): SimTimeDraft | null {
  const samples = hydrateSimPhaseHistory(history).sort((a, b) => sampleTime(a.atUtc) - sampleTime(b.atUtc));
  if (samples.length < 2) return null;
  const start = sampleTime(samples[0].atUtc);
  const end = sampleTime(samples[samples.length - 1].atUtc);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end - start < SIM_DRAFT_MIN_SPAN_MS) return null;

  let airMs = 0;
  let taxiMs = 0;
  let offBlocksUtc: string | undefined;
  let airborneUtc: string | undefined;
  let landedUtc: string | undefined;
  let onBlocksUtc: string | undefined;
  let sawAir = false;

  for (let i = 1; i < samples.length; i++) {
    const prev = samples[i - 1];
    const cur = samples[i];
    const dt = sampleTime(cur.atUtc) - sampleTime(prev.atUtc);
    const maxGap =
      sampleIsAirborne(prev) && sampleIsAirborne(cur)
        ? SIM_DRAFT_AIR_GAP_MS
        : sampleIsMoving(prev) && sampleIsMoving(cur)
          ? SIM_DRAFT_TAXI_GAP_MS
          : SIM_DRAFT_GAP_MS;
    if (!Number.isFinite(dt) || dt <= 0 || dt > maxGap) continue;
    const bucket = classify(prev);
    if (bucket === "air") {
      airMs += dt;
      if (!airborneUtc) airborneUtc = prev.atUtc;
      sawAir = true;
    } else if (bucket === "taxi") {
      taxiMs += dt;
    }
    if (!offBlocksUtc && sampleIsMoving(prev)) offBlocksUtc = prev.atUtc;
    if (!offBlocksUtc && sampleIsMoving(cur)) offBlocksUtc = cur.atUtc;
    if (sawAir && !landedUtc && !sampleIsAirborne(cur) && sampleIsAirborne(prev)) landedUtc = cur.atUtc;
    if (sampleIsMoving(cur)) onBlocksUtc = undefined;
    else if (offBlocksUtc && !onBlocksUtc && (sawAir || classify(cur) === "parked")) onBlocksUtc = cur.atUtc;
  }

  if (!onBlocksUtc && offBlocksUtc && classify(samples[samples.length - 1]) === "parked") {
    onBlocksUtc = samples[samples.length - 1].atUtc;
  }
  if (!landedUtc && sawAir && !sampleIsAirborne(samples[samples.length - 1])) {
    landedUtc = samples[samples.length - 1].atUtc;
  }

  const airTimeHours = hoursFromMs(airMs);
  const groundMovementHours = hoursFromMs(taxiMs);
  const engineHours = hoursFromMs(airMs + taxiMs);
  let blockHours: number | null = null;
  if (offBlocksUtc && onBlocksUtc) {
    const blockMs = sampleTime(onBlocksUtc) - sampleTime(offBlocksUtc);
    if (blockMs > 0 && blockMs <= 24 * 3600_000) blockHours = hoursFromMs(blockMs);
  }
  if (blockHours == null && engineHours > 0) blockHours = engineHours;

  const fuel = simFuelActualFromHistory(samples);
  if (airTimeHours <= 0 && engineHours <= 0 && groundMovementHours <= 0) return null;

  const bits = [`Draft from ${samples.length} SimConnect samples`];
  if (airTimeHours > 0 && groundMovementHours > 0) bits.push("taxi + air");
  else if (airTimeHours > 0) bits.push("air");
  else bits.push("ground movement");
  bits.push("Confirm before file.");

  void now;
  return {
    source: "simconnect",
    airTimeHours: airTimeHours > 0 ? airTimeHours : null,
    engineHours: engineHours > 0 ? engineHours : null,
    groundMovementHours: groundMovementHours > 0 ? groundMovementHours : null,
    blockHours,
    offBlocksUtc,
    airborneUtc,
    landedUtc,
    onBlocksUtc,
    note: bits.join(" · "),
    sampleCount: samples.length,
    fuelUpGal: fuel?.fuelUpGal ?? null,
    fuelDownGal: fuel?.fuelDownGal ?? null,
    fuelUsedGal: fuel?.fuelUsedGal ?? null,
  };
}

export function simFuelActualFromHistory(history: SimPhaseSample[]): SimFuelActual | null {
  const samples = hydrateSimPhaseHistory(history);
  let start: number | null = null;
  let last: number | null = null;
  let used: number | null = null;
  for (const row of samples) {
    const qty = asNum(row.fuelTotalGal);
    if (qty != null) {
      if (start == null) start = qty;
      last = qty;
    }
    const burn = asNum(row.fuelUsedGal);
    if (burn != null) used = burn;
  }
  if (used == null && start != null && last != null && start >= last) {
    used = Math.round((start - last) * 10) / 10;
  }
  if (start == null && last == null && used == null) return null;
  return { fuelUpGal: start, fuelDownGal: last, fuelUsedGal: used };
}

function icaoOrUndef(value?: string): string | undefined {
  const icao = value?.trim().toUpperCase();
  return icao && /^[A-Z0-9]{3,4}$/.test(icao) ? icao : undefined;
}

/**
 * Prefill identity from an on-file trip / log only. Never invents a route.
 */
export function draftPostFlightFromSim(input: {
  history: SimPhaseSample[];
  logs: FlightLogEntry[];
  missions?: Mission[];
  fleet?: AircraftSpec[];
  now?: Date;
  telemetrySession?: TailTelemetrySession | null;
  telemetryTrip?: TailTripTelemetry | null;
  /** Closed sessions. The draft uses the one for `missionId` / `flightLogId`. */
  telemetryTrips?: readonly TailTripTelemetry[] | null;
  missionId?: string;
  flightLogId?: string;
}): SimLogDraft | null {
  const times = estimateSimTimes(input.history, input.now);
  const targeted = Boolean(input.missionId || input.flightLogId);
  const targetLog =
    (input.flightLogId ? input.logs.find((row) => row.id === input.flightLogId) : undefined) ??
    (input.missionId ? input.logs.find((row) => row.tripId === input.missionId) : undefined);
  const targetMission =
    (input.missionId ? input.missions?.find((row) => row.id === input.missionId) : undefined) ??
    (targetLog?.tripId ? input.missions?.find((row) => row.id === targetLog.tripId) : undefined);
  const fallbackMission = targeted
    ? undefined
    : (input.missions?.find((m) => m.status === "in-progress") ??
      input.missions?.find((m) => m.status === "completed") ??
      input.missions?.find((m) => m.status === "pending"));
  const mission = targetMission ?? fallbackMission;
  const fromMission = mission ? input.logs.find((l) => l.tripId === mission.id) : undefined;
  const open = input.logs.filter((l) => l.status === "filed" || l.status === "airborne");
  const log =
    targetLog ?? fromMission ?? (targeted ? undefined : lastFindableLog(open.length ? open : input.logs));

  const targetTripId = mission?.id ?? log?.tripId ?? input.missionId;
  const targetLogId = log?.id ?? input.flightLogId;
  const trips = [
    ...(input.telemetryTrips ?? []),
    ...(input.telemetryTrip ? [input.telemetryTrip] : []),
  ];
  const closed =
    (targetLogId ? trips.find((row) => row.flightLogId === targetLogId) : undefined) ??
    (targetTripId ? trips.find((row) => row.tripId === targetTripId) : undefined) ??
    (!targeted ? input.telemetryTrip ?? undefined : undefined);

  const session = input.telemetrySession ?? null;
  const sessionMatches = !targeted
    ? true
    : Boolean(
        session &&
          ((targetTripId && session.tripId === targetTripId) ||
            (targetLogId && session.flightLogId === targetLogId)),
      );
  const live = sessionMatches ? liveSessionAsDraft(session) : null;
  if (!times && !live && !closed && !log?.offBlocksUtc) return null;

  const originIcao = icaoOrUndef(log?.originIcao) ?? icaoOrUndef(mission?.originIcao) ?? icaoOrUndef(closed?.originIcao);
  const destIcao = icaoOrUndef(log?.destIcao) ?? icaoOrUndef(mission?.destIcao) ?? icaoOrUndef(closed?.destIcao);
  const registration = log?.registration || mission?.registration || closed?.registration || undefined;
  const callsign = log?.callsign || mission?.callsign || closed?.callsign || undefined;
  const tail = registration
    ? input.fleet?.find((a) => a.registration === registration) ?? input.fleet?.find((a) => a.id === registration)
    : undefined;
  const fuelUnit = resolveAircraftFuelUnit(tail);
  const fuelLb = targeted
    ? (closed?.fuelUsedAmount ?? live?.fuelUsedLb ?? null)
    : (live?.fuelUsedLb ?? closed?.fuelUsedAmount ?? null);
  const fuel = prefillFuelUsedFromSimQty({ fuelUsedLb: fuelLb, unit: fuelUnit });
  const fuelUsedGal = fuel ? fuelUsedToUsGal(fuel.amount, fuel.unit) : null;

  const airTimeHours = targeted
    ? (closed?.airTimeHours ?? live?.airTimeHours ?? times?.airTimeHours ?? null)
    : (live?.airTimeHours ?? closed?.airTimeHours ?? times?.airTimeHours ?? null);
  const preciseEngine = targeted
    ? (closed?.engineHours ?? live?.engineHours ?? times?.engineHours ?? null)
    : (live?.engineHours ?? closed?.engineHours ?? times?.engineHours ?? null);
  const engineMx = targeted
    ? (closed?.engineMxHours ?? live?.engineMxHours ?? null)
    : (live?.engineMxHours ?? closed?.engineMxHours ?? null);
  const engineHours = engineMx != null && engineMx > 0 ? engineMx : preciseEngine;
  const engineDetail = targeted
    ? (closed?.engineDetail ?? live?.engineDetail ?? null)
    : (live?.engineDetail ?? closed?.engineDetail ?? null);
  const blockHours = targeted
    ? (closed?.blockHours ?? live?.blockHours ?? times?.blockHours ?? null)
    : (live?.blockHours ?? closed?.blockHours ?? times?.blockHours ?? null);
  const apuHours = targeted
    ? (closed?.apuHours ?? live?.apuHours ?? null)
    : (live?.apuHours ?? closed?.apuHours ?? null);
  const engineSource = (targeted ? closed?.engineSource ?? live?.engineSource : live?.engineSource ?? closed?.engineSource) ?? undefined;

  const bits = [times?.note ?? "Draft from SimConnect telemetry"];
  if (engineDetail) bits.push(engineDetail);
  if (engineSource === "combustion") bits.push("engine from combustion");
  else if (engineSource === "n1") bits.push("engine from N1 (combustion missing)");
  else if (engineSource === "phase_fallback") bits.push("engine from taxi+air (combustion / N1 missing)");
  if (fuel) bits.push(`fuel ${formatFuelFigure(fuel.amount, fuel.unit)}`);
  if (apuHours != null) bits.push("APU from RPM");
  else bits.push("APU blank — var missing or unused");
  bits.push("Edit before submit.");

  return {
    source: "simconnect",
    airTimeHours,
    engineHours,
    engineDetail,
    groundMovementHours: times?.groundMovementHours ?? null,
    blockHours,
    apuHours,
    fuelUsedAmount: fuel?.amount ?? null,
    fuelUsedUnit: fuel?.unit,
    fuelUpGal: times?.fuelUpGal ?? null,
    fuelDownGal: times?.fuelDownGal ?? null,
    fuelUsedGal: fuelUsedGal ?? times?.fuelUsedGal ?? null,
    engineSource,
    offBlocksUtc: closed?.offBlocksUtc ?? log?.offBlocksUtc ?? times?.offBlocksUtc,
    airborneUtc: closed?.airborneUtc ?? log?.wheelsOffUtc ?? times?.airborneUtc,
    landedUtc: closed?.landedUtc ?? log?.touchdownUtc ?? times?.landedUtc,
    onBlocksUtc: closed?.onBlocksUtc ?? log?.onBlocksUtc ?? times?.onBlocksUtc,
    note: bits.join(" · "),
    sampleCount: times?.sampleCount ?? 0,
    originIcao,
    destIcao,
    registration,
    callsign,
    flightLogId: log?.id ?? closed?.flightLogId,
    aircraftId: tail?.id ?? closed?.aircraftId,
  };
}

export function simDraftHoursLabel(draft: Pick<SimTimeDraft, "airTimeHours" | "engineHours" | "groundMovementHours" | "blockHours">): string {
  const parts: string[] = [];
  if (draft.airTimeHours != null) parts.push(`${formatDraftHours(draft.airTimeHours)} air`);
  if (draft.engineHours != null) parts.push(`${formatDraftHours(draft.engineHours)} eng`);
  if (draft.groundMovementHours != null) parts.push(`${formatDraftHours(draft.groundMovementHours)} taxi`);
  if (draft.blockHours != null) parts.push(`${formatDraftHours(draft.blockHours)} blk`);
  return parts.join(" / ") || "—";
}
